import i18next from "i18next";

import { IDashboardKpi } from "../../../../@types/admin";
import { SkeletonLoading } from "../../../../components/Skeleton";
import { KpiCardBase, KpiDelta, KpiLabel, KpiValue } from "../AdminHome.styles";

type Accent = "primary" | "secondary" | "success" | "warning";

interface IKpiCardProps {
  kpi?: IDashboardKpi;
  accent: Accent;
  isLoading?: boolean;
  label?: string;
}

function formatValue(kpi: IDashboardKpi): string {
  const value = kpi.value ?? 0;
  // Use the runtime default locale so both EU and US users get correct
  // number formatting (e.g. "1,240" vs "1.240"). Don't hard-code en-US.
  if (kpi.formatter === "percent") return `${value}%`;
  if (kpi.formatter === "days") {
    // Round to one decimal place so we don't render values like "8.049 days".
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return i18next.t("reports_kpis_days", { count: value, value: formatted });
  }
  return value.toLocaleString();
}

function getDirection(deltaPct: number): "up" | "down" | "flat" {
  if (deltaPct > 0) return "up";
  if (deltaPct < 0) return "down";
  return "flat";
}

const DIRECTION_ARROW: Record<"up" | "down" | "flat", string> = {
  up: "▲",
  down: "▼",
  flat: "→",
};

export function KpiCard({ kpi, accent, isLoading, label }: IKpiCardProps) {
  if (isLoading || !kpi) {
    return (
      <KpiCardBase accent={accent}>
        <KpiLabel>
          {label ?? <SkeletonLoading width={120} height={12} />}
        </KpiLabel>
        <SkeletonLoading width={100} height={28} />
        <SkeletonLoading width={160} height={12} />
      </KpiCardBase>
    );
  }

  // deltaPct is null when the previous window had 0 and the current doesn't —
  // a percentage doesn't make sense, so render "—" and a neutral arrow.
  const hasDelta = kpi.deltaPct !== null && kpi.deltaPct !== undefined;
  const direction = hasDelta ? getDirection(kpi.deltaPct as number) : "flat";
  const arrow = DIRECTION_ARROW[direction];
  const deltaLabel = hasDelta
    ? i18next.t("reports_kpis_delta", {
        percent: Math.abs(kpi.deltaPct as number).toFixed(1),
      })
    : i18next.t("reports_kpis_noDelta");

  return (
    <KpiCardBase accent={accent}>
      <KpiLabel>{label ?? kpi.label}</KpiLabel>
      <KpiValue>{formatValue(kpi)}</KpiValue>
      <KpiDelta direction={direction}>
        <span>{arrow}</span>
        <span>{deltaLabel}</span>
      </KpiDelta>
    </KpiCardBase>
  );
}
